// Copyright (c) 2026 Nathan Blackman / Find My Trout. All rights reserved.

import React, { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import TopNav from './components/TopNav';
import Auth from './components/Auth';
import Home from './pages/Home';
import Map from './pages/Map';
import Favorites from './pages/Favorites';
import Community from './pages/Community';
import Profile from './pages/Profile';
import Chat from './pages/Chat';
import './styles/main.css';

const PAGES_REQUIRING_ACCOUNT = ['favorites', 'chat', 'profile'];

function AppContent() {
  const [currentPage, setCurrentPage] = useState('home');
  const [profileUserId, setProfileUserId] = useState(null);
  const [chatWithUserId, setChatWithUserId] = useState(null);
  const [pendingPageAfterAuth, setPendingPageAfterAuth] = useState(null);
  const { currentUser, userProfile } = useAuth();

  const handleNavigate = (page) => {
    if (page === 'auth') {
      setPendingPageAfterAuth(null);
      setCurrentPage('auth');
      return;
    }
    if (!currentUser && PAGES_REQUIRING_ACCOUNT.includes(page)) {
      setPendingPageAfterAuth(page);
      setCurrentPage('auth');
      return;
    }
    if (page === 'profile') setProfileUserId(null);
    if (page === 'chat') clearChatWithUser();
    setCurrentPage(page);
  };

  const openUserProfile = (uid) => {
    if (!currentUser) {
      setPendingPageAfterAuth('profile');
      setProfileUserId(uid || null);
      setCurrentPage('auth');
      return;
    }
    setProfileUserId(uid || null);
    setCurrentPage('profile');
  };

  const openChatWithUser = (uid) => {
    if (!currentUser) {
      setPendingPageAfterAuth('chat');
      setChatWithUserId(uid || null);
      setCurrentPage('auth');
      return;
    }
    setChatWithUserId(uid || null);
    setCurrentPage('chat');
  };

  const clearProfileUserId = () => setProfileUserId(null);
  const clearChatWithUser = () => setChatWithUserId(null);

  useEffect(() => {
    if (!currentUser || currentPage !== 'auth') return;
    const next = pendingPageAfterAuth ?? 'home';
    setPendingPageAfterAuth(null);
    setCurrentPage(next);
    // Intentionally omit pendingPageAfterAuth from deps: including it re-runs the effect after
    // clearing pending while currentPage may still be 'auth', incorrectly sending users to home.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, currentPage]);

  useEffect(() => {
    if (currentUser) return;
    if (!PAGES_REQUIRING_ACCOUNT.includes(currentPage)) return;
    setCurrentPage('home');
    setProfileUserId(null);
    setChatWithUserId(null);
  }, [currentUser, currentPage]);

  if (currentPage === 'auth') {
    return (
      <Auth
        onBack={() => {
          setCurrentPage('home');
          setPendingPageAfterAuth(null);
        }}
      />
    );
  }

  return (
    <div className="app-content">
      <TopNav
        currentPage={currentPage}
        setCurrentPage={handleNavigate}
        user={currentUser}
        userProfile={userProfile}
      />
      <div className="app-content-body">
        {currentPage === 'home' && <Home setCurrentPage={handleNavigate} />}
        {currentPage === 'map' && <Map />}
        {currentPage === 'favorites' && <Favorites setCurrentPage={handleNavigate} />}
        {currentPage === 'community' && (
          <Community setCurrentPage={handleNavigate} openUserProfile={openUserProfile} openChatWithUser={openChatWithUser} />
        )}
        {currentPage === 'profile' && (
          <Profile
            viewingUserId={profileUserId}
            openUserProfile={openUserProfile}
            openChatWithUser={openChatWithUser}
            onBackToOwnProfile={clearProfileUserId}
          />
        )}
        {currentPage === 'chat' && (
          <Chat setCurrentPage={handleNavigate} initialOtherUserId={chatWithUserId} />
        )}
      </div>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
